import { Menu, Portal } from '@chakra-ui/react'
import cn from 'clsx'
import { Dispatch, SetStateAction } from 'react'
import { Controller, useForm } from 'react-hook-form'

import ArrowDown2 from '@/assets/icons/arrow-down-2.svg'
import Cancel from '@/assets/icons/cancel.svg'

import { LANGUAGES } from '@/constants/languages'

import Button from '../button/Button'
import Typography from '../typography/Typography'

interface SelectWithButtonProps {
	onConfirm: (targetLang: string) => void
	availableLangs: string[]
	setIsChoosingLanguage: (value: boolean) => void
	text?: string
	isTranslateTo?: boolean
	startButtonText?: string
	isCloseButton?: boolean
	setTranslatingLang?: Dispatch<SetStateAction<string>>
	classNameBlock?: string
}

const SelectWithButton = ({
	onConfirm,
	availableLangs,
	setIsChoosingLanguage,
	text,
	isTranslateTo,
	startButtonText,
	isCloseButton = true,
	setTranslatingLang,
	classNameBlock
}: SelectWithButtonProps) => {
	const defaultTranslateTo = availableLangs[0] || 'en'

	const { control, handleSubmit } = useForm({
		defaultValues: {
			translateFrom: isTranslateTo ? defaultTranslateTo : 'original'
		}
	})

	return (
		<form
			onSubmit={handleSubmit(data => {
				if (isTranslateTo) setTranslatingLang?.(data.translateFrom)
				onConfirm(data.translateFrom)
			})}
		>
			<div
				className={cn(
					'flex items-center border rounded-[16px] justify-between mt-10',
					classNameBlock
				)}
			>
				<div className='flex items-center gap-1 p-7'>
					<Typography variant='text-semibold' className='text-base'>
						{text || 'Translate from'}
					</Typography>

					<Controller
						name='translateFrom'
						control={control}
						render={({ field: { value, onChange } }) => (
							<Menu.Root positioning={{ placement: 'bottom-start' }}>
								<Menu.Trigger asChild>
									<div className='flex items-center gap-2 cursor-pointer'>
										<Button
											className='!text-black !p-0 !border-b !rounded-none'
											size='lg'
										>
											<Typography
												as='span'
												variant='text-semibold'
												className='text-base'
											>
												{LANGUAGES[value]}?
											</Typography>
										</Button>
										<ArrowDown2 />
									</div>
								</Menu.Trigger>
								<Portal>
									<Menu.Positioner>
										<Menu.Content>
											{availableLangs.map(lang => (
												<Menu.Item
													value={LANGUAGES[lang]}
													key={lang}
													onClick={() => {
														onChange(lang)
													}}
												>
													{lang === 'original' ? 'Original' : LANGUAGES[lang]}
												</Menu.Item>
											))}
										</Menu.Content>
									</Menu.Positioner>
								</Portal>
							</Menu.Root>
						)}
					/>
				</div>

				<div className='flex gap-4 py-[22px] pr-7'>
					<Button
						type='submit'
						className='!bg-black hover:!bg-gray-500 !text-white !rounded-[8px] !py-[10px] !px-8'
					>
						{startButtonText || 'OK'}
					</Button>
					{isCloseButton && (
						<Button
							onClick={() => setIsChoosingLanguage(false)}
							className='!p-0'
						>
							<Cancel className='rounded-full hover:bg-gray-500' />
						</Button>
					)}
				</div>
			</div>
		</form>
	)
}

export default SelectWithButton
